import { CRITERION_TEXT, type ClosingCommonDto, type PhaseQuestionDto, type PhaseTenDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { PhaseCommandBar } from '../tenant/wizard/Comandi';
import { Changed, Checks, doneIn, Origins, PhaseFooter, PhaseHead, Prepared, Questions, Talk } from './Fase';
import type { PhasePageProps } from './fasi-pagine';
import { Neurons, Timeline } from './Sinapsi';
import './chiusura.css';

/**
 * La fase 10 «La diagnosi» (contratto n. 1 versione 2.2, art. 8, fase 10; ADR 0179), identica alla pagina di prova
 * docs/mockup/percorso-sinapsi/13-fase10: una riga per criterio, gli scarti delle fasi 2-9 per reparto, il confronto con
 * il settore, le domande «[voce] oggi c'è?», «Parliamone», il registro intero. Ogni dato arriva dal server. I rimandi al
 * contratto stanno solo nelle pagine di prova (art. 4.1). La cornice usa i pezzi dell'ossatura (Fase.tsx, ADR 0174):
 * testa, «Come l'ho preparata», bloccanti, domande, «Parliamone», «Non va bene» e «Confermo la fase».
 */
export function Fase10Page(props: ClosingPageProps) {
  const p = useClosingPhase<PhaseTenDto>(10);
  return (
    <ClosingFrame p={p} props={props} eyebrow="Fase 10 · La diagnosi" working="Raccolgo gli scarti e scrivo la diagnosi"
      title={(d) => `Dove ${d.azienda ?? 'l’azienda'} di oggi è lontana da quella al suo meglio`}
      intro={(d) => `Solo sugli scarti probabili che pesano di più.${d.non_chiesti ? ` Gli altri ${d.non_chiesti} probabili restano probabili e dichiarati.` : ''} Se non rispondi, «non c’è» diventa una scelta di Timonio: lo scarto resta «probabile», resta nella diagnosi e ha la sua proposta nella fase 11.`}
    >
      {(d) => <Diagnosis d={d} />}
    </ClosingFrame>
  );
}

/** Il corpo della diagnosi: anche nel documento scaricabile (Progetto.tsx). */
export function Diagnosis({ d }: { d: PhaseTenDto }) {
  const count = (n: number, one: string, many: string) => (n ? `${n} ${n === 1 ? one : many}` : null);
  return (
    <>
      <section className="kpis">
        <Kpi label="SCARTI" n={String(d.numeri.scarti)} src="DALLE FASI 2-9, NON RICALCOLATI" />
        <Kpi label="CERTI" n={String(d.numeri.certi)} src="«NON C'È»: FONTE O RISPOSTA TUA" />
        <Kpi label="PROBABILI" n={String(d.numeri.probabili)} src="«LE FONTI NON LO MOSTRANO»" gold />
        <Kpi label="REPARTI" n={String(d.numeri.reparti)} src={d.numeri.reparti_per_sede.toUpperCase()} />
      </section>

      <section className="pan cy">
        <div className="hd">
          <h2 className="h2">Dove siete, criterio per criterio</h2>
        </div>
        <Rows cols="150px minmax(0,1fr) 150px" head={['CRITERIO', 'DOVE STA L’AZIENDA DI OGGI', 'SCARTI']}>
          {d.criteri.map((c) => [
            <b key="c">{CRITERION_TEXT[c.criterio]}</b>,
            c.dove_sta,
            <span key="n" className="m" style={{ fontSize: 12 }}>
              {[count(c.certi, 'certo', 'certi'), count(c.probabili, 'probabile', 'probabili'), count(c.superati, 'superato', 'superati')].filter(Boolean).join(' · ') || 'nessuno'}
            </span>,
          ])}
        </Rows>
      </section>

      <div className="hd">
        <h2 className="h2">Per reparto</h2>
      </div>
      {d.reparti.map((r) => (
        <article key={`${r.reparto}|${r.sede}`} className="pan">
          <div className="hd">
            <h3 className="h3" style={{ fontSize: 16 }}>
              {r.reparto}
              {r.sede ? ` · ${r.sede}` : ''}
            </h3>
            <span className="lbl">{r.scarti.length} SCARTI</span>
          </div>
          {r.scarti.length ? (
            <Rows cols="130px minmax(0,1.8fr) 64px 110px 60px" head={['CRITERIO', 'SCARTO · VOCE', 'FASE', 'GRADO', 'PESO']}>
              {r.scarti.map((x) => [
                <span key="c" className="lbl">
                  {CRITERION_TEXT[x.criterio].toUpperCase()}
                </span>,
                <span key="t">
                  <a href={`#/fase/${x.voce.fase}`} title={x.perche ?? x.fonte ?? undefined}>
                    {x.testo}
                    {x.vale_anche_per.length ? ` · vale anche per ${x.vale_anche_per.join(', ')}` : ''}
                  </a>
                  {x.nota && <span className="d">{x.nota}</span>}
                </span>,
                <span key="f" className="m" style={{ fontSize: 12 }}>
                  fase {x.fase}
                </span>,
                <span key="g" className={`chip ${x.certezza === 'superato' ? 'ok' : `gold${x.certezza === 'certo' ? ' pien' : ''}`}`}>
                  {x.certezza.toUpperCase()}
                </span>,
                <span key="p" className="m" style={{ fontSize: 12 }}>
                  {x.peso}
                </span>,
              ])}
            </Rows>
          ) : (
            <p className="note">{r.perche_nessuno}</p>
          )}
        </article>
      ))}
      {d.note.length > 0 && <p className="note">{d.note.join(' ')}</p>}

      <section className="pan">
        <div className="hd">
          <h2 className="h2">Il confronto con il settore</h2>
        </div>
        <Rows cols="minmax(0,1.2fr) 150px 150px minmax(0,1.2fr)" head={['RAPPORTO', (d.azienda ?? 'L’AZIENDA').toUpperCase(), 'SETTORE', 'FONTE']}>
          {d.settore.map((r) => [
            <b key="r">{r.rapporto}</b>,
            <span key="a">
              <b className="m">{r.azienda}</b>
              {r.calcolo && <span className="d m">{r.calcolo}</span>}
            </span>,
            <span key="s" className="m">
              {r.settore}
            </span>,
            <span key="f" className="src">
              {(r.fonte ?? '—').toUpperCase()}
            </span>,
          ])}
        </Rows>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ i pezzi comuni delle fasi 10 e 11 */

export type ClosingPageProps = PhasePageProps;

/** La pagina di una fase 10 o 11 (come `usePhase` dell'ossatura, che è per le fasi 1-3): si aggiorna finché si prepara. */
export function useClosingPhase<T extends ClosingCommonDto>(n: 10 | 11) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const q = useTenantQuery<T>(['fase', n], `/onboarding/fase/${n}`, { refetchInterval: (d) => (!d || d.stato === 'in_preparazione' || d.stato === 'si_prepara' || !!d.novita_in_corso?.length ? 2500 : false) });
  const reload = () => qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  return {
    q,
    reload,
    confirm: () => api<T>(t(`/onboarding/fase/${n}/conferma`), { method: 'POST' }).then(reload),
    redo: (motivo: string) => api<T>(t(`/onboarding/fase/${n}/rifai`), { method: 'POST', body: { motivo } }).then(reload),
  };
}

/**
 * La cornice di una pagina 10 o 11, nell'ordine delle pagine di prova 13 e 14, con i pezzi dell'ossatura: testa, «Come
 * l'ho preparata», bloccanti, ciò che è cambiato, il corpo, «cose che aiuterebbero qui», domande, «Parliamone», il
 * registro intero, «da dove viene», la fine.
 */
export function ClosingFrame<T extends ClosingCommonDto>({
  p,
  props,
  eyebrow,
  working,
  title,
  intro,
  children,
  footer,
  extra,
  confirmLabel,
  allegato,
  allegatoAperto,
  onAllegato,
}: {
  p: ReturnType<typeof useClosingPhase<T>>;
  props: PhasePageProps;
  eyebrow: string;
  working: string;
  title: (d: T) => string;
  /** La riga sotto «Cosa chiedo a te». */
  intro: (d: T) => string;
  children: (d: T) => ReactNode;
  /** Ciò che la fase aggiunge prima della fine (fase 11: l'offerta del contratto n. 2). */
  footer?: (d: T) => ReactNode;
  /** I tasti accanto a «Confermo la fase» (fase 11: il documento scaricabile). */
  extra?: ReactNode;
  /** Il testo del tasto di conferma (fase 11: «Confermo e chiudo l'incarico», art. 12.2). */
  confirmLabel?: string;
  /**
   * La pagina in sintesi (fase 11, consegna): in vista solo `children`, le domande e `footer`; tutto il resto (questo
   * dettaglio, i controlli, «Cosa è cambiato», «Cosa manca», le fonti e il registro) sta nell'allegato chiuso.
   */
  allegato?: { titolo: string; nota: string; corpo: (d: T) => ReactNode };
  allegatoAperto?: boolean;
  onAllegato?: (open: boolean) => void;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = allegatoAperto ?? ownOpen;
  const setOpen = onAllegato ?? setOwnOpen;
  const { tenant } = useTenant();
  const d = p.q.data;
  if (!d) return p.q.error ? <p className="sn-err">{errorMessage(p.q.error)}</p> : <p className="sn-note">Apro la fase…</p>;
  if (d.stato !== 'pronta')
    return (
      <div className="sn-main">
        {d.tempi && !d.tempi.fine ? (
          <Timeline t={d.tempi} eyebrow="Il consulente al lavoro" title={working} />
        ) : (
          <section className="sn-panel">
            <p className="sn-ey cyan">{eyebrow}</p>
            <h1 className="sn-h1 sm">{working}</h1>
            <p className="sn-note">{d.stato === 'errore' ? d.manca[0] : props.stopped ? 'Hai fermato la fase: riprendila dai Comandi quando vuoi, o vai avanti con ciò che c’è.' : 'Si prepara mentre leggi la fase prima: la pagina si apre da sola.'}</p>
          </section>
        )}
        <PhaseCommandBar />
        <Neurons tenantId={tenant.id} />
      </div>
    );
  if (allegato)
    return (
      <main className="sn-main snc" id={`f${d.fase}`}>
        <PhaseHead eyebrow={`${eyebrow}${doneIn(d.tempi, d)}`} title={title(d)} level={d.livello} ultimo={d.ultimo_giro} consegnata={d.consegnata_il} />
        {/* In cima al più una riga: il resto di «Cosa è cambiato» e «Cosa manca» sta nell'allegato. */}
        {(d.rifatta || d.cambiato.length > 0) && (
          <p className="note">
            {d.rifatta ? 'La fase è stata rifatta con il tuo motivo.' : `${d.cambiato.length === 1 ? 'Una novità' : `${d.cambiato.length} novità`} dall’ultima volta.`} Il dettaglio è nell’allegato, in fondo.
          </p>
        )}
        {children(d)}
        <Questions domande={d.domande} limiti={d.limiti} intro={intro(d)} onChange={p.reload} empty="Nessuna domanda per te in questa fase." />
        {footer?.(d)}
        <section className="pan allegato" id={`f${d.fase}-allegato`}>
          <div className="hd">
            <h2 className="h2">{allegato.titolo}</h2>
            <button type="button" className="btn2" aria-expanded={open} onClick={() => setOpen(!open)}>
              {open ? 'Chiudi l’allegato' : 'Apri l’allegato'}
            </button>
          </div>
          {!open && <p className="note">{allegato.nota}</p>}
        </section>
        {open && (
          <>
            {allegato.corpo(d)}
            <Fold titolo="Controlli, novità e fonti" sotto={`${d.cambiato.length} novità · ${d.manca.length} cose che mancano · ${d.provenienza.length} strati di fonti`}>
              {d.tempi?.fine && <Prepared t={d.tempi} ondata={d.ondata} />}
              <Checks list={d.bloccanti} />
              <Changed list={d.cambiato} missing={d.manca} />
              <Helps d={d} />
              <RegistryAll list={d.registro_intero} limit={d.limiti.per_incarico} />
              <Origins list={d.provenienza} />
            </Fold>
          </>
        )}
        <Talk fase={d.fase} />
        <PhaseFooter rifatta={d.rifatta} confermata={d.confermata} canConfirm onRedo={p.redo} onConfirm={p.confirm} next={props.next} onNext={props.onNext} sealing={props.sealing} extra={extra} confirmLabel={confirmLabel} />
      </main>
    );
  return (
    <main className="sn-main snc" id={`f${d.fase}`}>
      <PhaseHead eyebrow={`${eyebrow}${doneIn(d.tempi, d)}`} title={title(d)} level={d.livello} ultimo={d.ultimo_giro} consegnata={d.consegnata_il} />
      {d.tempi?.fine && <Prepared t={d.tempi} ondata={d.ondata} />}
      <Checks list={d.bloccanti} />
      <Changed list={d.cambiato} missing={d.manca} />
      {children(d)}
      <Helps d={d} />
      <Questions domande={d.domande} limiti={d.limiti} intro={intro(d)} onChange={p.reload} empty="Nessuna domanda per te in questa fase." />
      <Talk fase={d.fase} />
      <RegistryAll list={d.registro_intero} limit={d.limiti.per_incarico} />
      <Origins list={d.provenienza} />
      {footer?.(d)}
      <PhaseFooter rifatta={d.rifatta} confermata={d.confermata} canConfirm onRedo={p.redo} onConfirm={p.confirm} next={props.next} onNext={props.onNext} sealing={props.sealing} extra={extra} confirmLabel={confirmLabel} />
    </main>
  );
}

/** Una parte dell'allegato che si apre con un clic: il titolo e una riga su cosa c'è dentro. */
export function Fold({ titolo, sotto, children, open: start = false }: { titolo: string; sotto: string; children: ReactNode; open?: boolean }) {
  const [open, setOpen] = useState(start);
  return (
    <section className="pan fold">
      <button type="button" className="fold-hd" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        <span className="h2">{titolo}</span>
        <span className="lbl">{open ? 'CHIUDI' : 'APRI'}</span>
      </button>
      {!open && <p className="note">{sotto}</p>}
      {open && <div className="fold-body">{children}</div>}
    </section>
  );
}

export function Kpi({ label, n, src, gold = false, small = false }: { label: string; n: string; src: string; gold?: boolean; small?: boolean }) {
  return (
    <div className="kpi">
      <p className="lbl">{label}</p>
      <p className={`n ${small ? 's' : ''}`}>{n}</p>
      <p className={`src ${gold ? 's' : ''}`}>{src}</p>
    </div>
  );
}

/** Le righe delle pagine di prova: intestazione, celle con l'etichetta per il telefono (`data-l`). */
export function Rows({ cols, head, children }: { cols: string; head: string[]; children: ReactNode[][] }) {
  return (
    <div className="rows">
      <div className="row head" style={{ gridTemplateColumns: cols }}>
        {head.map((h) => (
          <span key={h}>{h}</span>
        ))}
      </div>
      {children.map((cells, i) => (
        <div key={i} className="row" style={{ gridTemplateColumns: cols }}>
          {cells.map((c, k) => (
            <span key={k} data-l={head[k]}>
              {c}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

const STATE: Record<string, { testo: string; cls: string }> = {
  aperta: { testo: 'APERTA', cls: 'gold' },
  scelta_timonio: { testo: 'SCELTA DI TIMONIO', cls: 'gold dash' },
  decisa: { testo: 'SCELTA DI TIMONIO', cls: 'gold dash' },
  risposta: { testo: 'RISPOSTA DEL TITOLARE', cls: 'vi' },
  superata: { testo: 'SUPERATA', cls: 'dim' },
  risolta: { testo: 'SUPERATA', cls: 'dim' },
  non_chiesta: { testo: 'NON CHIESTA', cls: 'dim' },
};
const stateOf = (s: string) => STATE[s] ?? { testo: s.toUpperCase(), cls: '' };
/** Lo stato come stringa: i cinque stati del registro (ADR 0174) e quelli della fase 0 di prima. */
const stOf = (q: PhaseQuestionDto): string => q.stato;

/** «Cose che aiuterebbero qui» (art. 7-bis.13): voci del registro di fasi già fatte, non domande nuove. */
function Helps({ d }: { d: ClosingCommonDto }) {
  if (!d.aiuterebbero.length) return null;
  return (
    <section className="pan">
      <div className="hd">
        <h2 className="h2">Cose che aiuterebbero qui</h2>
      </div>
      <p className="sub">Non sono domande nuove: sono voci del registro di fasi già fatte. Se rispondi, questa fase si aggiorna da sola, senza rifarla.</p>
      <div className="rows">
        {d.aiuterebbero.map((h) => (
          <div key={h.domanda_id} className="row" style={{ gridTemplateColumns: 'minmax(0,1.4fr) 70px 150px minmax(0,1.2fr) 80px' }}>
            <span data-l="VOCE">
              <b>{h.voce}</b>
            </span>
            <span data-l="FASE" className="m" style={{ fontSize: 13 }}>
              fase {h.fase}
            </span>
            <span data-l="STATO">
              <span className={`chip ${stateOf(h.stato).cls}`}>{stateOf(h.stato).testo}</span>
            </span>
            <span data-l="COSA CAMBIEREBBE QUI" className="note">
              {h.cosa_cambierebbe}
            </span>
            <a href={`#/fase/${h.fase}`} style={{ fontSize: 13 }}>
              Rispondi
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Il registro delle domande intero, con i cinque stati (art. 7-bis.11): le fasi 10 e 11 sono «La tua azienda». */
function RegistryAll({ list, limit }: { list: PhaseQuestionDto[]; limit: number }) {
  const asked = list.filter((q) => stOf(q) !== 'superata' && stOf(q) !== 'risolta' && stOf(q) !== 'non_chiesta').length;
  return (
    <details className="pan" id="registro">
      <summary className="hd" style={{ cursor: 'pointer' }}>
        <h2 className="h2">Il registro delle domande</h2>
        <span className="chips">
          <span className="lbl">
            {asked} DOMANDE IN TUTTO L'INCARICO, AL MASSIMO {limit}
          </span>
        </span>
      </summary>
      <Rows cols="54px minmax(0,1.1fr) minmax(0,1.4fr) 160px minmax(0,1fr)" head={['FASE', 'VOCE', 'DOMANDA', 'STATO', 'RISPOSTA']}>
        {list.map((q) => [
          <span key="f" className="m" style={{ fontSize: 13 }}>
            {q.fase}
          </span>,
          q.voce,
          q.domanda,
          <span key="s" className={`chip ${stateOf(q.stato).cls}`}>
            {stateOf(q.stato).testo}
          </span>,
          <span key="r" className="note">
            {q.risposta ? `${q.risposta.testo ?? q.risposta.chiave}${stOf(q) === 'scelta_timonio' || stOf(q) === 'decisa' ? ' (predefinita)' : ''}` : '—'}
          </span>,
        ])}
      </Rows>
    </details>
  );
}
